import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "motion/react";
import FlashcardFeatureCard from "./FlashcardFeatureCard";
import TrackFeatureCard from "./TrackFeatureCard";
import PractiseFeatureCard from "./PractiseFeatureCard";
import NotionFeatureCard from "./NotionFeatureCard";
import GoogleFeatureCard from "./GoogleFeatureCard";

const FeatureSection = () => {
  const featureRef = useRef(null);
  const horizontalScrollRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(true);
  const [shouldScroll, setShouldScroll] = useState(true);
  const [isTallScreen, setIsTallScreen] = useState(false);

  // Calculate total width needed for cards (5 cards × 300px + 4 gaps × 64px = 1756px)
  const TOTAL_CARDS_WIDTH = 1756;

  useEffect(() => {
    const handleResize = () => {
      const isDesktopView = window.innerWidth >= 640;
      setIsDesktop(isDesktopView);
      // Check if screen can fit all cards (accounting for padding)
      setShouldScroll(window.innerWidth < TOTAL_CARDS_WIDTH + 64); // Adding padding
      // Check if screen height is above typical MacBook height
      setIsTallScreen(window.innerHeight > 2000);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { scrollYProgress: scrollYProgressHeading } = useScroll({
    target: featureRef,
    offset: ["start end", "end start"],
  });

  const width = useTransform(
    scrollYProgressHeading,
    [0, 0.25],
    ["120px", "0px"]
  );
  const barHeight = useTransform(
    scrollYProgressHeading,
    [0, 0.25],
    ["16px", "12px"]
  );

  const { scrollYProgress: scrollYProgressHorizontal } = useScroll({
    target: horizontalScrollRef,
    offset: isTallScreen ? ["start 30%", "end end"] : ["start -5%", "end end"],
  });

  // Add smoothing to the horizontal scroll with gentle transitions
  const smoothX = useSpring(
    useTransform(scrollYProgressHorizontal, [0, 1], ["0%", "-60%"]),
    { stiffness: 150, damping: 30, restDelta: 0.001 }
  );

  return (
    <>
      <main
        className="bg-white z-[20] relative w-full pt-4 3xl:pt-0"
        ref={featureRef}
      >
        <div
          ref={horizontalScrollRef}
          className={`bg-[#E8F0F9] mx-auto w-full rounded-t-2xl sm:rounded-t-[64px] ${
            shouldScroll
              ? "min-h-[120vh] sm:min-h-[250vh] 3xl:min-h-fit"
              : "min-h-fit"
          } md:pt-14`}
        >
          {/* Sticky container for both heading and cards */}
          <div className="sm:h-[1100px] sm:sticky sm:top-0 bg-[#E8F0F9] md:pt-24 py-8">
            {/* Heading Section */}
            <h1 className="font-editorial font-extrabold text-3xl tracking-[-0.05em] z-[100] text-center sm:hidden">
              Study However You Want
            </h1>
            <div className="flex 3xl:justify-center items-center gap-x-1.5 px-4 max-3xl:pl-24 w-full">
              <div className="hidden sm:flex items-center gap-x-1.5">
                <h1 className="font-editorial font-extrabold text-4xl md:text-5xl lg:text-[68px] tracking-[-0.05em] z-[100] text-start 3xl:text-[3.5vw]">
                  Study However You
                </h1>

                <motion.div
                  className="bg-black rounded-md hidden sm:block"
                  style={{
                    width,
                    height: barHeight,
                  }}
                ></motion.div>

                <h1 className="font-editorial font-extrabold text-4xl md:text-5xl lg:text-[68px] tracking-[-0.05em] z-[100] text-start 3xl:text-[3.5vw]">
                  Want
                </h1>
              </div>
            </div>

            {/* Cards Section */}
            <div className="flex sm:items-center overflow-hidden px-4 mt-8 sm:mt-16 md:mt-36 sm:pl-8">
              <motion.div
                className={`flex sm:flex-row flex-col sm:gap-16 gap-6 w-full ${
                  shouldScroll ? "sm:w-auto" : "sm:w-full sm:justify-center"
                } items-center will-change-transform`}
                style={{
                  x: isDesktop && shouldScroll ? smoothX : 0,
                }}
              >
                <FlashcardFeatureCard />
                <TrackFeatureCard />
                <PractiseFeatureCard />
                <GoogleFeatureCard />
                <NotionFeatureCard />
              </motion.div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default FeatureSection;
